import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  List as ListIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { GAME_SERVICE_URL } from '../../services/workerUrls';

interface TestResponse {
  success: boolean;
  operation?: string;
  data?: any;
  message?: string;
  error?: string;
  stack?: string;
  details?: any;
}

export function PineconeTestPanel() {
  const [loading, setLoading] = useState(false);
  const [statusData, setStatusData] = useState<TestResponse | null>(null);
  const [indexResult, setIndexResult] = useState<TestResponse | null>(null);
  const [searchResult, setSearchResult] = useState<TestResponse | null>(null);

  // Test document fields
  const [testContent, setTestContent] = useState(
    'This is a test document for Pinecone vector search. It contains information about the ancient trading routes of the Zephyr System.'
  );
  const [testTitle, setTestTitle] = useState('Test Document');
  const [testCategory, setTestCategory] = useState('general');

  // Search fields
  const [searchQuery, setSearchQuery] = useState('trading routes');
  const [searchTopK, setSearchTopK] = useState('5');

  // Reindex/audit state
  const [reindexBucket, setReindexBucket] = useState<'lore' | 'history' | 'all'>('all');
  const [reindexResult, setReindexResult] = useState<TestResponse | null>(null);
  const [auditResult, setAuditResult] = useState<TestResponse | null>(null);
  const [filenameAuditResult, setFilenameAuditResult] = useState<TestResponse | null>(null);

  // Delete/rename dialogs
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; bucket: 'lore' | 'history'; key: string } | null>(null);
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; bucket: 'lore' | 'history'; oldKey: string; newKey: string } | null>(null);

  // Load status on mount
  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    const url = `${GAME_SERVICE_URL()}/api/test-pinecone/status`;
    console.log('[PineconeTest] Loading status from:', url);

    try {
      const response = await fetch(url);
      console.log('[PineconeTest] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[PineconeTest] HTTP error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('[PineconeTest] Status data:', data);
      setStatusData(data);
    } catch (error) {
      console.error('[PineconeTest] Failed to load status:', error);
      setStatusData({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load status',
        details: {
          url,
          errorType: error?.constructor?.name,
          gameServiceUrl: GAME_SERVICE_URL(),
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const testIndex = async () => {
    setLoading(true);
    setIndexResult(null);
    try {
      const response = await fetch(`${GAME_SERVICE_URL()}/api/test-pinecone/index`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: testContent,
          title: testTitle,
          category: testCategory,
        }),
      });
      const data = await response.json();
      setIndexResult(data);
    } catch (error) {
      setIndexResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to test indexing',
      });
    } finally {
      setLoading(false);
    }
  };

  const testSearch = async () => {
    setLoading(true);
    setSearchResult(null);
    try {
      const response = await fetch(`${GAME_SERVICE_URL()}/api/test-pinecone/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: searchQuery,
          topK: parseInt(searchTopK, 10),
          category: testCategory === 'all' ? undefined : testCategory,
        }),
      });
      const data = await response.json();
      setSearchResult(data);
    } catch (error) {
      setSearchResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to test search',
      });
    } finally {
      setLoading(false);
    }
  };

  const runReindex = async () => {
    setLoading(true);
    setReindexResult(null);
    try {
      const response = await fetch(`${GAME_SERVICE_URL()}/api/test-pinecone/reindex`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucket: reindexBucket }),
      });
      const data = await response.json();
      setReindexResult(data);
      // Refresh audit after reindex
      if (data.success) {
        await loadAudit();
      }
    } catch (error) {
      setReindexResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reindex',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAudit = async () => {
    setLoading(true);
    setAuditResult(null);
    try {
      const response = await fetch(`${GAME_SERVICE_URL()}/api/test-pinecone/audit?bucket=all`);
      const data = await response.json();
      setAuditResult(data);
    } catch (error) {
      setAuditResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load audit',
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async (bucket: 'lore' | 'history', key: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${GAME_SERVICE_URL()}/api/test-pinecone/file`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucket, key }),
      });
      const data = await response.json();
      if (data.success) {
        await loadAudit(); // Refresh list
      } else {
        alert(`Delete failed: ${data.error}`);
      }
    } catch (error) {
      alert(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
      setDeleteDialog(null);
    }
  };

  const renameFile = async (bucket: 'lore' | 'history', oldKey: string, newKey: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${GAME_SERVICE_URL()}/api/test-pinecone/file/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucket, oldKey, newKey }),
      });
      const data = await response.json();
      if (data.success) {
        await loadAudit(); // Refresh list
        await loadFilenameAudit(); // Refresh filename audit
      } else {
        alert(`Rename failed: ${data.error}`);
      }
    } catch (error) {
      alert(`Rename failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
      setRenameDialog(null);
    }
  };

  const loadFilenameAudit = async () => {
    setLoading(true);
    setFilenameAuditResult(null);
    try {
      const response = await fetch(`${GAME_SERVICE_URL()}/api/test-pinecone/audit-filenames?bucket=all`);
      const data = await response.json();
      setFilenameAuditResult(data);
    } catch (error) {
      setFilenameAuditResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to audit filenames',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStatusCard = () => {
    if (!statusData) return null;

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <InfoIcon color="primary" />
            <Typography variant="h6">Pinecone Status</Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadStatus}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>

          {statusData.success ? (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">Configured:</Typography>
                <Chip
                  label={statusData.data?.configured ? 'Yes' : 'No'}
                  color={statusData.data?.configured ? 'success' : 'error'}
                  size="small"
                />
              </Stack>
              {statusData.data?.configured && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">Namespace:</Typography>
                    <Chip label={statusData.data?.namespace} size="small" />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">Environment:</Typography>
                    <Chip label={statusData.data?.environment} size="small" />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">Index Host:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {statusData.data?.indexHost}
                    </Typography>
                  </Stack>
                </>
              )}
              {statusData.message && (
                <Alert severity="info" sx={{ mt: 1 }}>{statusData.message}</Alert>
              )}
            </Stack>
          ) : (
            <Alert severity="error">
              <Typography variant="body2" gutterBottom>
                <strong>Error:</strong> {statusData.error || 'Unknown error'}
              </Typography>
              {statusData.details && (
                <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  <strong>Debug Info:</strong><br />
                  URL: {statusData.details.url}<br />
                  Game Service URL: {statusData.details.gameServiceUrl}<br />
                  Error Type: {statusData.details.errorType}<br />
                  {statusData.details.hasApiKey !== undefined && (
                    <>Has API Key: {statusData.details.hasApiKey ? 'Yes' : 'No'}<br /></>
                  )}
                  {statusData.details.hasIndexHost !== undefined && (
                    <>Has Index Host: {statusData.details.hasIndexHost ? 'Yes' : 'No'}<br /></>
                  )}
                </Box>
              )}
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Check Console Logs panel for more details
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderIndexTest = () => {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <PlayIcon color="primary" />
            <Typography variant="h6">Test Indexing</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Test indexing a document to Pinecone and view the raw response.
          </Typography>

          <Stack spacing={2} mb={2}>
            <TextField
              label="Document Title"
              value={testTitle}
              onChange={(e) => setTestTitle(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Category"
              value={testCategory}
              onChange={(e) => setTestCategory(e.target.value)}
              size="small"
              fullWidth
              helperText="system, faction, commodity, npc, or general"
            />
            <TextField
              label="Document Content"
              value={testContent}
              onChange={(e) => setTestContent(e.target.value)}
              multiline
              rows={4}
              size="small"
              fullWidth
            />
          </Stack>

          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={testIndex}
            disabled={loading || !statusData?.data?.configured}
            fullWidth
          >
            {loading ? 'Indexing...' : 'Test Index'}
          </Button>

          {indexResult && (
            <Box mt={2}>
              {indexResult.success ? (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label="Success" color="success" size="small" />
                      <Typography variant="body2">{indexResult.message}</Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        p: 1,
                        backgroundColor: 'background.default',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        maxHeight: 400,
                        overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(indexResult, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Alert severity="error">
                  {indexResult.error}
                  {indexResult.stack && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.65rem',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      {indexResult.stack}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderSearchTest = () => {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <SearchIcon color="primary" />
            <Typography variant="h6">Test Search</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Test searching Pinecone and view the raw response.
          </Typography>

          <Stack spacing={2} mb={2}>
            <TextField
              label="Search Query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Top K Results"
              value={searchTopK}
              onChange={(e) => setSearchTopK(e.target.value)}
              type="number"
              size="small"
              fullWidth
              helperText="Number of results to return (1-10)"
            />
          </Stack>

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={testSearch}
            disabled={loading || !statusData?.data?.configured}
            fullWidth
          >
            {loading ? 'Searching...' : 'Test Search'}
          </Button>

          {searchResult && (
            <Box mt={2}>
              {searchResult.success ? (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label="Success" color="success" size="small" />
                      <Typography variant="body2">{searchResult.message}</Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {/* Results Summary */}
                      {searchResult.data?.results?.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>Results:</Typography>
                          {searchResult.data.results.map((result: any, idx: number) => (
                            <Card key={idx} sx={{ mb: 1, backgroundColor: 'background.default' }}>
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                  <Chip label={`Score: ${result.score.toFixed(4)}`} size="small" color="primary" />
                                  <Typography variant="caption" color="text.secondary">
                                    {result.path}
                                  </Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                  {result.snippet}
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      )}

                      <Divider />

                      {/* Raw Response */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>Raw Response:</Typography>
                        <Box
                          sx={{
                            p: 1,
                            backgroundColor: 'background.default',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            maxHeight: 400,
                            overflow: 'auto',
                          }}
                        >
                          {JSON.stringify(searchResult, null, 2)}
                        </Box>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Alert severity="error">
                  {searchResult.error}
                  {searchResult.stack && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.65rem',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      {searchResult.stack}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderReindexCard = () => {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <SyncIcon color="primary" />
            <Typography variant="h6">Re-index R2 to Pinecone</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Scan R2 buckets and re-index all documents to Pinecone. Use this to fix sync mismatches.
          </Typography>

          <Stack spacing={2} mb={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Bucket</InputLabel>
              <Select
                value={reindexBucket}
                label="Bucket"
                onChange={(e) => setReindexBucket(e.target.value as 'lore' | 'history' | 'all')}
              >
                <MenuItem value="all">All Buckets</MenuItem>
                <MenuItem value="lore">Lore Only</MenuItem>
                <MenuItem value="history">History Only</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={runReindex}
            disabled={loading || !statusData?.data?.configured}
            fullWidth
          >
            {loading ? 'Re-indexing...' : 'Re-index R2 to Pinecone'}
          </Button>

          {reindexResult && (
            <Box mt={2}>
              {reindexResult.success ? (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label="Success" color="success" size="small" />
                      <Typography variant="body2">{reindexResult.message}</Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>Lore:</strong> {reindexResult.data?.lore?.indexed || 0} indexed, {reindexResult.data?.lore?.errors || 0} errors, {reindexResult.data?.lore?.skipped || 0} skipped
                      </Typography>
                      <Typography variant="body2">
                        <strong>History:</strong> {reindexResult.data?.history?.indexed || 0} indexed, {reindexResult.data?.history?.errors || 0} errors, {reindexResult.data?.history?.skipped || 0} skipped
                      </Typography>
                      <Typography variant="body2">
                        <strong>Batch size:</strong> {reindexResult.data?.batchSize || 10}
                      </Typography>
                      {reindexResult.data?.hasMore && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          More files remain. Click "Re-index" again to continue.
                        </Alert>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Alert severity="error">
                  {reindexResult.error}
                  {reindexResult.error?.includes('Too many subrequests') && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Batch size too large. Try reducing the batch size or run multiple smaller batches.
                    </Typography>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderFilenameAuditCard = () => {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <SearchIcon color="primary" />
            <Typography variant="h6">Filename Audit</Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadFilenameAudit}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Check if R2 filenames match the friendly IDs in their content. One-click rename to fix mismatches.
          </Typography>

          {!filenameAuditResult && (
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={loadFilenameAudit}
              disabled={loading}
              fullWidth
            >
              Run Filename Audit
            </Button>
          )}

          {filenameAuditResult && (
            <Box mt={2}>
              {filenameAuditResult.success ? (
                <Stack spacing={2}>
                  <Alert severity={
                    (filenameAuditResult.data?.lore?.mismatches?.length || 0) +
                    (filenameAuditResult.data?.history?.mismatches?.length || 0) === 0
                      ? 'success'
                      : 'warning'
                  }>
                    {filenameAuditResult.message}
                  </Alert>

                  {/* Lore Mismatches */}
                  {filenameAuditResult.data?.lore?.mismatches?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom color="warning.main">
                        Lore Bucket Mismatches ({filenameAuditResult.data.lore.mismatches.length})
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Current Key</TableCell>
                              <TableCell>Suggested Key</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filenameAuditResult.data.lore.mismatches.map((mismatch: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                  {mismatch.currentKey}
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {mismatch.reason}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                  {mismatch.suggestedKey || '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {mismatch.suggestedKey && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => renameFile('lore', mismatch.currentKey, mismatch.suggestedKey)}
                                      disabled={loading}
                                    >
                                      Fix
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* History Mismatches */}
                  {filenameAuditResult.data?.history?.mismatches?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom color="warning.main">
                        History Bucket Mismatches ({filenameAuditResult.data.history.mismatches.length})
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Current Key</TableCell>
                              <TableCell>Extracted ID</TableCell>
                              <TableCell>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filenameAuditResult.data.history.mismatches.map((mismatch: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                  {mismatch.currentKey}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                  {mismatch.extractedId || '-'}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" color="text.secondary">
                                    {mismatch.reason}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Alert severity="error">{filenameAuditResult.error}</Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderAuditCard = () => {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <ListIcon color="primary" />
            <Typography variant="h6">R2 File Browser</Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadAudit}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" mb={2}>
            List, delete, and rename files in R2 buckets.
          </Typography>

          {!auditResult && (
            <Button
              variant="outlined"
              startIcon={<ListIcon />}
              onClick={loadAudit}
              disabled={loading}
              fullWidth
            >
              Load Files
            </Button>
          )}

          {auditResult && (
            <Box mt={2}>
              {auditResult.success ? (
                <Stack spacing={2}>
                  {/* Lore Bucket */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Lore Bucket ({auditResult.data?.lore?.count || 0} files)
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Key</TableCell>
                            <TableCell align="right">Size</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {auditResult.data?.lore?.files?.map((file: any) => (
                            <TableRow key={file.key}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {file.key}
                              </TableCell>
                              <TableCell align="right">{(file.size / 1024).toFixed(2)} KB</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => setRenameDialog({ open: true, bucket: 'lore', oldKey: file.key, newKey: file.key })}
                                  title="Rename"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteDialog({ open: true, bucket: 'lore', key: file.key })}
                                  title="Delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* History Bucket */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      History Bucket ({auditResult.data?.history?.count || 0} files)
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Key</TableCell>
                            <TableCell align="right">Size</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {auditResult.data?.history?.files?.map((file: any) => (
                            <TableRow key={file.key}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {file.key}
                              </TableCell>
                              <TableCell align="right">{(file.size / 1024).toFixed(2)} KB</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => setRenameDialog({ open: true, bucket: 'history', oldKey: file.key, newKey: file.key })}
                                  title="Rename"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteDialog({ open: true, bucket: 'history', key: file.key })}
                                  title="Delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Stack>
              ) : (
                <Alert severity="error">{auditResult.error}</Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Card
      className="panel"
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <CardContent className="panel-header" sx={{ p: 1.5, pb: 1 }}>
        <Typography
          variant="overline"
          sx={{
            color: 'info.main',
            fontWeight: 700,
            letterSpacing: 1,
            fontSize: '0.65rem',
            display: 'block',
            mb: 0.5,
          }}
        >
          Pinecone Test Panel
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Test Pinecone vector search indexing and retrieval with diagnostic information
        </Typography>
      </CardContent>

      <Box className="panel-scroll">
        {loading && !statusData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {renderStatusCard()}
        {renderIndexTest()}
        {renderSearchTest()}
        {renderReindexCard()}
        {renderFilenameAuditCard()}
        {renderAuditCard()}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Check the browser console and Cloudflare Worker logs for detailed Pinecone API request/response information.
          </Typography>
        </Alert>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog?.open || false}
        onClose={() => setDeleteDialog(null)}
      >
        <DialogTitle>Delete File?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteDialog?.key}</strong> from the {deleteDialog?.bucket} bucket?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteDialog && deleteFile(deleteDialog.bucket, deleteDialog.key)}
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialog?.open || false}
        onClose={() => setRenameDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rename File</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Rename <strong>{renameDialog?.oldKey}</strong> in the {renameDialog?.bucket} bucket.
          </DialogContentText>
          <TextField
            label="New File Key"
            value={renameDialog?.newKey || ''}
            onChange={(e) => renameDialog && setRenameDialog({ ...renameDialog, newKey: e.target.value })}
            fullWidth
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => renameDialog && renameFile(renameDialog.bucket, renameDialog.oldKey, renameDialog.newKey)}
            disabled={loading || !renameDialog?.newKey || renameDialog?.newKey === renameDialog?.oldKey}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
